import {
  ILiverampDistributionListInfo,
  LiverampDistributionAPIKey,
} from '~/src/dash/api/liveramp-distribution/liveramp_distribution_model';
import {
  formatSelectedToArrayOfStrings,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import {
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  BooleanToString,
  CustomStatus,
  EllipsisWithTooltipRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';

interface ILiverampDistributionListViewTable {
  columns: IListViewTableColumns;
}

export const formatBiddersResponseIntoLVTRows = (
  bidders: ILiverampDistributionListInfo[],
  cfg: ILiverampDistributionListViewTable,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  bidders.forEach((bidder) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = bidder[responseKey as keyof ILiverampDistributionListInfo];
      if (key === 'StatusDistributed') {
        if (value === 0) {
          value = 'Requested';
        } else {
          value = 'Distributed';
        }
      }
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

export interface ILiverampDistributionViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys?: LiverampDistributionAPIKey[];
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: LiverampDistributionAPIKey[]; // what keys should be made active on reset
  };
  filters: ICustomAPIFilterConfig;
  isInitialized?: boolean;
  sorts: LiverampDistributionAPIKey[];
  table: ILiverampDistributionListViewTable;
}

export const liverampConfig: ILiverampDistributionViewConfig = {
  cypressClassName: 'bidderslistview',
  columnSelector: {
    resetKeys: ['StatusDistributed', 'AssociationLiverampSegmentID'],
  },
  filters: {
    'Segment Status': {
      queryKey: 'status',
      formatRequestValue: formatSelectedToArrayOfStrings,
      defaultSelected: ['0'],
    },
  },
  defaultState: {
    activeColumnKeys: ['StatusDistributed', 'AssociationLiverampSegmentID'],
    isTableExpanded: true,
    order: 'desc',
    orderBy: '',
    page: 0,
    rowsPerPage: 10,
  },
  sorts: [],
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'SegmentID',
        responseKey: 'SegmentID',
        valueType: 'string',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Segment Name',
        requestKey: 'Name',
        responseKey: 'Name',
        valueType: 'string',
      },
      StatusDistributed: {
        cellJSX: CustomStatus,
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        key: 'StatusDistributed',
        label: 'Status',
        requestKey: 'AssociationLiverampSegmentID',
        responseKey: 'AssociationLiverampSegmentID',
        valueType: 'string',
        cellRenderType: 'text_center',
        disableSort: true,
      },
      Actions: {
        disableSort: true,
        cellRenderType: 'actions',
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
      AssociationLiverampSegmentID: {
        cellJSX: BooleanToString,
        disableSort: true,
        cellRenderType: 'custom_actions',
        headerRenderType: 'actions',
        key: 'AssociationLiverampSegmentID',
        label: 'Action',
        requestKey: 'AssociationLiverampSegmentID',
        responseKey: 'AssociationLiverampSegmentID',
        valueType: 'component',
      },
    },
  },
};
